import type {
  PanderrNavigationItem,
  PanderrTemplateDefinition,
} from "../types";

import type {
  PanderrFooterSiteData,
} from "./footer";

export interface PanderrHeaderSiteData
  extends PanderrFooterSiteData {
  logoUrl?: string;
  websiteUrl?: string;
  defaultMetaDescription?: string;
  navigation?: PanderrNavigationItem[];

  /**
   * Optional Panderr-managed content connection.
   *
   * Used by published sites that should resolve selected assets from the
   * project's managed-content roles after deployment.
   */
  managedContentProjectId?: string;
}

function escapeHtml(value: string): string {
  return String(value || "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

const DEFAULT_NAVIGATION = [
  { label: "About", href: "/about/" },
  { label: "Services", href: "/services/" },
  { label: "Our Work", href: "/our-work/" },
  { label: "Contact", href: "/contact/" },
];

const DEFAULT_CTA = {
  label: "Get Started",
  href: "/contact/",
  variant: "primary" as const,
};

function renderNavigationItem(item: PanderrNavigationItem): string {
  const children = item.children || [];

  if (children.length === 0) {
    return `
      <a class="panderr-nav-link" href="${escapeHtml(item.href)}">
        ${escapeHtml(item.label)}
      </a>
    `;
  }

  return `
    <details class="panderr-nav-dropdown">
      <summary>${escapeHtml(item.label)}</summary>

      <div class="panderr-nav-dropdown-menu">
        ${
          item.href && item.href !== "#"
            ? `
              <a href="${escapeHtml(item.href)}">
                ${escapeHtml(item.label)} Overview
              </a>
            `
            : ""
        }

        ${children
          .map(
            (child) => `
              <a href="${escapeHtml(child.href)}">
                ${escapeHtml(child.label)}
              </a>
            `,
          )
          .join("")}
      </div>
    </details>
  `;
}

export function renderPanderrHeader(
  template: PanderrTemplateDefinition | undefined,
  siteData: PanderrHeaderSiteData = {},
): string {
  const navigation = siteData.navigation?.length
    ? siteData.navigation
    : template?.navigation?.length
      ? template.navigation
      : DEFAULT_NAVIGATION;

  const cta = template?.headerCta || DEFAULT_CTA;
  const showCta = cta.label.toLowerCase() !== "public documents";

  const businessName = siteData.businessName?.trim() || "Your Business";
  const logoUrl = siteData.logoUrl?.trim() || "";

  return `
    <header
      class="panderr-header"
      data-panderr-component="header"
      data-panderr-global-component="header"
    >
      <div class="panderr-shell-wide panderr-header-inner">
        <a
          class="panderr-brand"
          href="/"
          aria-label="Home"
          data-panderr-role="logo-slot"
          data-panderr-component="logo-slot"
        >
          ${
            logoUrl
              ? `<img
                  class="panderr-site-logo"
                  src="${escapeHtml(logoUrl)}"
                  alt="${escapeHtml(businessName)}"
                  data-panderr-site-logo="true"
                  data-panderr-role="logo"
                  data-panderr-component="logo"
                >`
              : `<span
                  class="panderr-text-brand"
                  data-panderr-role="text-brand"
                  data-panderr-component="text-brand"
                >${escapeHtml(businessName)}</span>`
          }
        </a>

<nav class="panderr-nav" aria-label="Primary navigation">
  ${navigation.map(renderNavigationItem).join("")}
</nav>

        ${showCta ? `
          <a
            class="panderr-button panderr-button-primary panderr-header-cta"
            href="${escapeHtml(cta.href)}"
          >
            ${escapeHtml(cta.label)}
          </a>
        ` : ""}

        <details class="panderr-mobile-navigation">
          <summary aria-label="Open navigation">
            <span></span>
            <span></span>
            <span></span>
          </summary>

          <div class="panderr-mobile-navigation-panel">
            ${navigation
              .map((item) => {
                const children = item.children || [];

                return `
                  <div class="panderr-mobile-navigation-group">
                    <a href="${escapeHtml(item.href)}">
                      ${escapeHtml(item.label)}
                    </a>

                    ${
                      children.length
                        ? `<div class="panderr-mobile-navigation-children">
                            ${children
                              .map(
                                (child) => `
                                  <a href="${escapeHtml(child.href)}">
                                    ${escapeHtml(child.label)}
                                  </a>
                                `,
                              )
                              .join("")}
                           </div>`
                        : ""
                    }
                  </div>
                `;
              })
              .join("")}

            ${showCta ? `
              <a
                class="panderr-button panderr-button-primary panderr-mobile-navigation-cta"
                href="${escapeHtml(cta.href)}"
              >
                ${escapeHtml(cta.label)}
              </a>
            ` : ""}
          </div>
        </details>
      </div>
    </header>
  `;
}

export function createHeaderCss(): string {
  return `
    .panderr-header {
      position: relative;
      z-index: 20;
      border-bottom: 1px solid var(--panderr-color-border);
      background: rgba(255,255,255,.96);
    }

    .panderr-header-inner {
      min-height: 84px;
      display: grid;
      grid-template-columns: 1fr auto 1fr;
      gap: 2rem;
      align-items: center;
    }

    .panderr-brand {
      display: inline-flex;
      align-items: center;
      gap: .8rem;
      width: fit-content;
      max-width: 100%;
      font-weight: 800;
    }

    .panderr-site-logo {
      display: block;
      width: auto;
      max-width: 190px;
      max-height: 58px;
      object-fit: contain;
      object-position: left center;
    }

    .panderr-text-brand {
      display: block;
      color: var(--panderr-color-text);
      font-family: var(--panderr-heading-font);
      font-size: 1.15rem;
      font-weight: 800;
      line-height: 1.1;
      text-decoration: none;
    }

    .panderr-nav {
      display: flex;
      gap: clamp(1rem, 1.7vw, 2rem);
      align-items: center;
      justify-content: center;
      font-size: .95rem;
      font-weight: 600;
    }

    .panderr-nav a {
      color: var(--panderr-color-text-muted);
      transition: color var(--panderr-transition-fast);
      white-space: nowrap;
    }

    .panderr-nav a:hover {
      color: var(--panderr-color-text);
    }

        .panderr-nav-dropdown {
      position: relative;
    }

    .panderr-nav-dropdown summary {
      display: flex;
      align-items: center;
      gap: .4rem;
      color: var(--panderr-color-text-muted);
      cursor: pointer;
      list-style: none;
      transition: color var(--panderr-transition-fast);
      white-space: nowrap;
    }

    .panderr-nav-dropdown summary::-webkit-details-marker {
      display: none;
    }

    .panderr-nav-dropdown summary::after {
      content: "";
      width: .45rem;
      height: .45rem;
      border-right: 1.5px solid currentColor;
      border-bottom: 1.5px solid currentColor;
      transform: translateY(-.15rem) rotate(45deg);
      transition: transform var(--panderr-transition-fast);
    }

    .panderr-nav-dropdown[open] summary,
    .panderr-nav-dropdown summary:hover {
      color: var(--panderr-color-text);
    }

    .panderr-nav-dropdown[open] summary::after {
      transform: translateY(.1rem) rotate(225deg);
    }

    .panderr-nav-dropdown-menu {
      position: absolute;
      z-index: 30;
      top: calc(100% + .8rem);
      left: 50%;
      display: grid;
      min-width: 220px;
      padding: .65rem;
      border: 1px solid var(--panderr-color-border);
      border-radius: 14px;
      background: #fff;
      box-shadow: 0 18px 48px rgba(20, 20, 20, .14);
      transform: translateX(-50%);
    }

    .panderr-nav-dropdown-menu a {
      display: block;
      padding: .75rem .85rem;
      border-radius: 9px;
    }

    .panderr-nav-dropdown-menu a:hover,
    .panderr-nav-dropdown-menu a:focus-visible {
      background: var(--panderr-color-surface);
    }

    .panderr-header-cta {
      justify-self: end;
    }

    @media (max-width: 1040px) {
      .panderr-nav {
        gap: 1rem;
        font-size: .88rem;
      }
    }

    @media (max-width: 820px) {
      .panderr-header-inner {
        grid-template-columns: 1fr auto;
      }

      .panderr-nav {
        display: none;
      }
    }

    .panderr-mobile-navigation {
      display: none;
      position: relative;
      justify-self: end;
    }

    .panderr-mobile-navigation > summary {
      display: grid;
      gap: 5px;
      width: 44px;
      height: 44px;
      padding: 11px 9px;
      border: 1px solid var(--panderr-color-border);
      border-radius: 8px;
      background: #fff;
      cursor: pointer;
      list-style: none;
    }

    .panderr-mobile-navigation > summary::-webkit-details-marker {
      display: none;
    }

    .panderr-mobile-navigation > summary span {
      display: block;
      width: 100%;
      height: 2px;
      background: currentColor;
    }

    .panderr-mobile-navigation-panel {
      position: absolute;
      z-index: 50;
      top: calc(100% + .75rem);
      right: 0;
      width: min(330px, calc(100vw - 2rem));
      padding: 1rem;
      border: 1px solid var(--panderr-color-border);
      border-radius: 14px;
      background: #fff;
      box-shadow: 0 20px 50px rgba(16,47,80,.18);
    }

    .panderr-mobile-navigation-group {
      border-bottom: 1px solid var(--panderr-color-border);
    }

    .panderr-mobile-navigation-group > a {
      display: block;
      padding: .85rem .2rem;
      color: var(--panderr-color-text);
      font-weight: 700;
    }

    .panderr-mobile-navigation-children {
      display: grid;
      gap: .15rem;
      padding: 0 0 .75rem .8rem;
    }

    .panderr-mobile-navigation-children a {
      padding: .4rem;
      color: var(--panderr-color-text-muted);
      font-size: .92rem;
    }

    .panderr-mobile-navigation-cta {
      display: flex;
      margin-top: 1rem;
    }

    /* Municipality Modern civic header */
    body[data-panderr-template-id="municipality-modern"] .panderr-header {
      border-bottom: 1px solid #dce3e8;
      background: #fff;
      box-shadow: 0 2px 12px rgba(16,47,80,.04);
    }

    body[data-panderr-template-id="municipality-modern"] .panderr-header-inner {
      min-height: 124px;
      grid-template-columns: minmax(175px, 1fr) auto minmax(170px, 1fr);
    }

    body[data-panderr-template-id="municipality-modern"] .panderr-site-logo {
      max-width: 245px;
      max-height: 78px;
    }

    body[data-panderr-template-id="municipality-modern"] .panderr-nav {
      gap: clamp(.85rem, 1.35vw, 1.55rem);
      font-size: .92rem;
    }

    body[data-panderr-template-id="municipality-modern"] .panderr-nav a,
    body[data-panderr-template-id="municipality-modern"] .panderr-nav-dropdown summary {
      color: #263746;
      font-weight: 650;
    }

    body[data-panderr-template-id="municipality-modern"] .panderr-nav a:hover,
    body[data-panderr-template-id="municipality-modern"] .panderr-nav-dropdown summary:hover {
      color: #173f69;
    }

    body[data-panderr-template-id="municipality-modern"] .panderr-nav-dropdown-menu {
      min-width: 255px;
      border-color: #dce3e8;
      border-radius: 8px;
      box-shadow: 0 18px 45px rgba(16,47,80,.16);
    }

    body[data-panderr-template-id="municipality-modern"] .panderr-nav-dropdown-menu a {
      border-radius: 5px;
    }

    body[data-panderr-template-id="municipality-modern"] .panderr-nav-dropdown-menu a:hover {
      background: #eef4f8;
      color: #173f69;
    }

    body[data-panderr-template-id="municipality-modern"] .panderr-header-cta {
      background: #173f69;
      border-color: #173f69;
      border-radius: 6px;
      padding: .8rem 1.1rem;
      font-size: .88rem;
      font-weight: 700;
      box-shadow: none;
    }

    body[data-panderr-template-id="municipality-modern"] .panderr-header-cta:hover {
      background: #102f50;
      border-color: #102f50;
    }

    @media (max-width: 1040px) {
      body[data-panderr-template-id="municipality-modern"] .panderr-header-inner {
        grid-template-columns: minmax(165px, 1fr) auto minmax(150px, .8fr);
        gap: 1rem;
      }

      body[data-panderr-template-id="municipality-modern"] .panderr-site-logo {
        max-width: 175px;
      }
    }

    @media (max-width: 820px) {
      .panderr-header-inner {
        grid-template-columns: 1fr auto;
      }

      .panderr-nav,
      .panderr-header-cta {
        display: none;
      }

      .panderr-mobile-navigation {
        display: block;
      }

      body[data-panderr-template-id="municipality-modern"] .panderr-header-inner {
        min-height: 82px;
        grid-template-columns: 1fr auto;
      }

      body[data-panderr-template-id="municipality-modern"] .panderr-site-logo {
        max-width: 170px;
        max-height: 60px;
      }
    }

    @media (max-width: 520px) {
      .panderr-site-logo {
        max-width: 150px;
        max-height: 50px;
      }

      body[data-panderr-template-id="municipality-modern"] .panderr-site-logo {
        max-width: 150px;
        max-height: 54px;
      }
    }
  `;
}
